'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { useTranslations } from '@/components/I18nProvider'
import { Button } from '@sarkiai/ui'
import { CheckCircle2, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'

export function VCardCTA() {
  const t = useTranslations('enterprise_page.vcard_cta')
  // @ts-ignore
  const features = t.raw('features') || []

  return (
    <section className="py-24 relative overflow-hidden px-4 lg:px-12">
      <div className="container mx-auto">
        <div className="bg-zinc-100 dark:bg-zinc-900 rounded-4xl overflow-hidden border border-black/5 dark:border-white/5 relative">
          <div className="flex flex-col lg:flex-row items-stretch">

            {/* Left Image Side - Full Width No Padding */}
            <div className="w-full lg:w-1/2 relative min-h-[400px] lg:min-h-[500px]">
              <div className="absolute inset-0">
                <Image
                  src="/assets/covers/djotta-cards-cover.webp"
                  alt="VCard"
                  fill
                  className="object-cover object-center"
                />
              </div>
            </div>

            {/* Right Content Side */}
            <div className="w-full lg:w-1/2 p-10 md:p-16 space-y-8 relative z-10 flex flex-col justify-center">
              <div className="space-y-6">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="text-4xl md:text-6xl uppercase tracking-tighter leading-none text-foreground"
                >
                  {t('title')}
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="text-lg text-muted-foreground leading-relaxed"
                >
                  {t('subtitle')}
                </motion.p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Array.isArray(features) && (features as string[]).map((feature: string, i: number) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full border border-black/10 dark:border-white/20 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-foreground" strokeWidth={1.5} />
                    </div>
                    <span className="text-sm uppercase tracking-tight text-foreground/80">{feature}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="pt-4"
              >
                <Button asChild size="default" className="h-12 px-6 rounded-2xl bg-black text-white hover:bg-black/90 transition-all uppercase tracking-widest text-xs">
                  <Link href="/vcard">
                    {t('cta')}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
